/**
 * Shared ECharts options for MicroEAI Explainability enterprise plots.
 * Plot 1: Source attribution. Plot 2: SHAP Top-K. Plot 4: Faithfulness gauge.
 * Plot 3 (LIME scorecard) is rendered as a table in the page.
 *
 * Colors align with Bias LLM charts (`biasLlmChartPalette`, same amber as verdict REVIEW in `BiasLLMPlotsSection`).
 */
import type { EChartsCoreOption } from 'echarts/core';
import { BIAS_GRAPH_BLUE, BIAS_GRAPH_GREEN, BIAS_GRAPH_RED } from './biasLlmChartPalette';
import { EXPLAINABILITY_CONFIG } from './explainabilityEnterprise.utils';
import type { SourceAttribution, ShapTopKItem } from './explainabilityEnterprise.utils';

const DOM_THR = EXPLAINABILITY_CONFIG.dominance_share;

/** Bias “REVIEW” / warning band (same as `BiasLLMPlotsSection` verdict middle color). */
const BIAS_REVIEW_AMBER = '#f59e0b';

/** Match Bias / Fairness Recharts: slate ticks, light grid, neutral axis lines. */
const AXIS_LABEL = '#64748b';
const AXIS_LINE = '#e2e8f0';
const SPLIT_LINE = '#f0f0f0';
/** Neutral category bars — slate (same family as axis labels). */
const NEUTRAL_BAR = '#94a3b8';

/**
 * Map source-attribution bucket names to the Bias LLM primary palette (governance-gate style:
 * primary / compliance → blue, policy-safe → green, residual / behavior → red).
 */
function sourceInfluenceColor(key: string): string {
  const k = String(key).toLowerCase();
  if (
    k.includes('prompt') ||
    k.includes('input') ||
    k.includes('instruction') ||
    k.includes('query') ||
    k.includes('user')
  ) {
    return BIAS_GRAPH_BLUE;
  }
  if (
    k.includes('metadata') ||
    k.includes('config') ||
    k.includes('system') ||
    k.includes('context') ||
    k.includes('param') ||
    k.includes('tool')
  ) {
    return BIAS_GRAPH_GREEN;
  }
  if (
    k.includes('output') ||
    k.includes('model') ||
    k.includes('response') ||
    k.includes('completion') ||
    k.includes('generation') ||
    k.includes('answer')
  ) {
    return BIAS_GRAPH_RED;
  }
  return NEUTRAL_BAR;
}

/** Faithfulness bar color: low = risk (red), mid = contextual (amber), high = strong (green) — mirrors Bias verdict tones. */
function faithfulnessQualityColor(meanAopc: number): string {
  if (meanAopc < 0.2) return BIAS_GRAPH_RED;
  if (meanAopc < 0.4) return BIAS_REVIEW_AMBER;
  return BIAS_GRAPH_GREEN;
}

export function getPlot1SourceAttributionOption(sourcePct: SourceAttribution): EChartsCoreOption | null {
  const keys = Object.keys(sourcePct);
  if (keys.length === 0) return null;
  const vals = keys.map(k => sourcePct[k] ?? 0);
  const allZero = vals.every(v => v === 0);
  // When all values are 0, use a small non-zero for visibility so categories are shown
  const displayVals = allZero ? vals.map(() => 0.02) : vals;

  const barColors = keys.map(k => sourceInfluenceColor(k));

  return {
    color: [BIAS_GRAPH_RED, BIAS_GRAPH_GREEN, BIAS_GRAPH_BLUE, NEUTRAL_BAR],
    tooltip: {
      trigger: 'axis',
      axisPointer: { lineStyle: { color: AXIS_LINE, width: 1 } },
      borderColor: AXIS_LINE,
      textStyle: { color: '#334155' },
      formatter: allZero
        ? (params: unknown) => {
            const p = Array.isArray(params) ? params[0] : params;
            const idx = (p as { dataIndex?: number })?.dataIndex ?? 0;
            return `${keys[idx]}: 0 (no influence)`;
          }
        : undefined
    },
    legend: {
      data: [
        { name: 'Prompt / input', icon: 'roundRect', itemStyle: { color: BIAS_GRAPH_BLUE } },
        { name: 'Metadata / config', icon: 'roundRect', itemStyle: { color: BIAS_GRAPH_GREEN } },
        { name: 'Output / model', icon: 'roundRect', itemStyle: { color: BIAS_GRAPH_RED } },
        { name: 'Other', icon: 'roundRect', itemStyle: { color: NEUTRAL_BAR } }
      ],
      bottom: 0,
      itemWidth: 20,
      itemHeight: 14,
      textStyle: { color: AXIS_LABEL }
    },
    grid: { left: '10%', right: '8%', bottom: '22%', top: '6%', containLabel: true },
    xAxis: {
      type: 'category',
      data: keys,
      axisLabel: { color: AXIS_LABEL },
      axisLine: { lineStyle: { color: AXIS_LINE } }
    },
    yAxis: {
      type: 'value',
      min: 0,
      max: 1,
      name: 'Influence share (0–1)',
      nameTextStyle: { color: AXIS_LABEL },
      axisLabel: { color: AXIS_LABEL },
      axisLine: { lineStyle: { color: AXIS_LINE } },
      splitLine: { lineStyle: { color: SPLIT_LINE } }
    },
    series: [
      {
        type: 'bar',
        data: displayVals.map((v, i) => ({
          value: v,
          itemStyle: { color: barColors[i], borderRadius: [4, 4, 0, 0] }
        })),
        barWidth: '50%',
        barMinHeight: 4
      }
    ]
  };
}

export function getPlot2ShapTopKOption(shapTopK: ShapTopKItem[]): EChartsCoreOption | null {
  if (shapTopK.length === 0) return null;

  const tokens = shapTopK.map(x => x.token);
  const rel = shapTopK.map(x => x.relativeInfluence);
  const colorMap: Record<string, string> = {
    Sensitive: BIAS_GRAPH_RED,
    Whitelisted: BIAS_GRAPH_GREEN,
    Neutral: NEUTRAL_BAR
  };
  const colors = shapTopK.map(x => colorMap[x.category] ?? NEUTRAL_BAR);

  const cues: string[] = [];
  if (rel.length > 0 && rel[0] >= DOM_THR) cues.push('Top token dominates → concentration risk ↑');
  if (shapTopK.some(x => x.category === 'Sensitive')) cues.push('Sensitive token in Top-K → governance review needed');
  if (cues.length === 0) cues.push('No major SHAP risk cues triggered');
  const cueText = 'Risk cues:\n• ' + cues.join('\n• ');

  return {
    /** Default series palette — Plot 2 bars override per item; legend uses matching swatches. */
    color: [BIAS_GRAPH_RED, BIAS_GRAPH_GREEN, NEUTRAL_BAR, BIAS_GRAPH_BLUE],
    tooltip: {
      trigger: 'axis',
      axisPointer: { lineStyle: { color: AXIS_LINE, width: 1 } },
      borderColor: AXIS_LINE,
      textStyle: { color: '#334155' }
    },
    legend: {
      data: [
        { name: 'Sensitive', icon: 'roundRect', itemStyle: { color: BIAS_GRAPH_RED } },
        { name: 'Whitelisted', icon: 'roundRect', itemStyle: { color: BIAS_GRAPH_GREEN } },
        { name: 'Neutral', icon: 'roundRect', itemStyle: { color: NEUTRAL_BAR } }
      ],
      bottom: 0,
      itemWidth: 20,
      itemHeight: 14,
      textStyle: { color: AXIS_LABEL }
    },
    grid: { left: '10%', right: '8%', bottom: '22%', top: '8%', containLabel: true },
    xAxis: {
      type: 'category',
      data: tokens,
      axisLabel: { color: AXIS_LABEL, rotate: tokens.length > 4 ? 25 : 0 },
      axisLine: { lineStyle: { color: AXIS_LINE } }
    },
    yAxis: {
      type: 'value',
      name: 'Relative influence (Top-K share)',
      nameTextStyle: { color: AXIS_LABEL },
      min: 0,
      max: Math.max(0.5, Math.max(...rel) + 0.1 || 0.6),
      axisLabel: { color: AXIS_LABEL },
      axisLine: { lineStyle: { color: AXIS_LINE } },
      splitLine: { lineStyle: { color: SPLIT_LINE } }
    },
    series: [
      {
        type: 'bar',
        data: rel.map((v, i) => ({ value: v, itemStyle: { color: colors[i] } })),
        barWidth: '55%',
        itemStyle: { borderRadius: [4, 4, 0, 0] },
        markLine: {
          symbol: 'none',
          data: [
            {
              yAxis: DOM_THR,
              name: `Dominance ${DOM_THR.toFixed(2)}`,
              lineStyle: { type: 'dashed', color: BIAS_GRAPH_BLUE, width: 1, opacity: 0.75 }
            }
          ]
        }
      }
    ],
    title: {
      text: cueText,
      left: '2%',
      top: '72%',
      textStyle: { fontSize: 9, fill: AXIS_LABEL }
    }
  };
}

export function getPlot4FaithfulnessGaugeOption(meanAopc: number): EChartsCoreOption | null {
  const interp = meanAopc < 0.2 ? 'Superficial' : meanAopc < 0.4 ? 'Contextual' : 'Strong';
  // When meanAopc is 0, use a small value for visual bar so it's visible; label still shows 0
  const displayVal = meanAopc === 0 ? 0.05 : meanAopc;
  const bandColor = faithfulnessQualityColor(meanAopc);

  return {
    color: [bandColor],
    tooltip: {
      trigger: 'axis',
      axisPointer: { lineStyle: { color: AXIS_LINE, width: 1 } },
      borderColor: AXIS_LINE,
      textStyle: { color: '#334155' }
    },
    grid: { left: '8%', right: '8%', bottom: '35%', top: '6%', containLabel: true },
    xAxis: {
      type: 'value',
      min: 0,
      max: 1,
      name: 'Faithfulness (higher = explanations truly affect output)',
      nameTextStyle: { color: AXIS_LABEL },
      axisLabel: { color: AXIS_LABEL },
      axisLine: { lineStyle: { color: AXIS_LINE } },
      splitLine: { lineStyle: { color: SPLIT_LINE } }
    },
    yAxis: {
      type: 'category',
      data: [''],
      show: false
    },
    series: [
      {
        type: 'bar',
        data: [displayVal],
        barWidth: '25%',
        barMinHeight: 8,
        itemStyle: { color: bandColor, borderRadius: [0, 4, 4, 0] },
        label: {
          show: true,
          position: 'right',
          formatter: `  ${meanAopc.toFixed(2)}`,
          fontWeight: 'bold',
          color: bandColor
        }
      }
    ],
    title: [
      {
        text: `Interpretation: ${interp} explanations${meanAopc === 0 ? ' (value: 0)' : ''}`,
        left: '2%',
        top: '72%',
        textStyle: { fontSize: 10, fill: '#334155' }
      },
      {
        text: 'Note: Low faithfulness can reflect distributed/contextual reasoning.\nFor descriptive use cases, contextual explanations may be acceptable.',
        left: '2%',
        top: '82%',
        textStyle: { fontSize: 9, fill: AXIS_LABEL }
      }
    ]
  };
}
